import React from "react";
import { useOutletContext } from "react-router-dom";
import { Reveal } from "@/components/site/Reveal";

export default function Manifesto() {
    const { openDemo } = useOutletContext();

    return (
        <div className="bg-warm">
            <section className="pt-20 pb-24 md:pt-24 md:pb-32 bg-warm">
                <div className="container-x max-w-4xl">
                    <Reveal>
                        <div className="text-sm text-slate-500 mb-4">Manifesto</div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="heading-hero text-5xl sm:text-6xl lg:text-7xl">
                            From automation
                            <br />
                            <span className="muted">to augmentation.</span>
                        </h1>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <p className="mt-12 text-slate-700 text-lg md:text-xl leading-relaxed">
                            For a decade, enterprise AI promised one thing: do the manual work, faster and cheaper.
                            That was automation, and it was real — but it was only the floor.
                        </p>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <p className="mt-6 text-slate-700 text-lg md:text-xl leading-relaxed">
                            The conversation has shifted. AI is no longer just reducing manual effort. It has become
                            a{" "}
                            <span className="text-slate-900 italic">decision-support layer</span> that sits across the
                            organization — helping people see more, decide faster, and act with confidence.
                        </p>
                    </Reveal>

                    <Reveal delay={0.3}>
                        <div className="my-20 mesh-frame p-10 md:p-14">
                            <div className="glass-card p-8 grid md:grid-cols-2 gap-8">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">
                                        Automation asks
                                    </div>
                                    <div
                                        className="heading-section text-2xl md:text-3xl text-slate-900"
                                    >
                                        How do we remove the human from this task?
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-900 mb-3">
                                        Augmentation asks
                                    </div>
                                    <div
                                        className="heading-section text-2xl md:text-3xl text-slate-900"
                                    >
                                        How do we make every human better at it?
                                    </div>
                                </div>
                            </div>
                            <div
                                className="mt-6 font-serif text-slate-900 text-xl text-center"
                            >
                                We built NyraAI for the second question.
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <h2 className="heading-section text-3xl md:text-4xl mt-16">
                            The decision-support layer.
                        </h2>
                        <p className="mt-6 text-slate-700 text-lg leading-relaxed">
                            The most valuable thing AI can do in an enterprise isn&apos;t to replace a process — it&apos;s
                            to stand beside the people running it. To retrieve the policy they&apos;d have searched ten
                            minutes for. To draft the reply they&apos;d have written from scratch. To flag the risk they
                            might have missed. The human still decides. The human is just{" "}
                            <span className="text-slate-900 italic">
                                better-informed, faster, and more consistent
                            </span>
                            .
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <h2 className="heading-section text-3xl md:text-4xl mt-16">
                            Augmentation has rules.
                        </h2>
                        <p className="mt-6 text-slate-700 text-lg leading-relaxed">
                            A decision-support layer earns trust only if it&apos;s grounded, governed, and accountable.
                            That&apos;s why NyraAI grounds every answer in your sources, scores its confidence, keeps a
                            human in the loop, and logs everything it does.{" "}
                            <span className="text-slate-900 italic">
                                Augmentation without governance is just a faster way to be wrong.
                            </span>
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="mt-20 border-l-2 border-slate-900 pl-6 md:pl-8">
                            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">
                                The shift we&apos;re building for
                            </div>
                            <div
                                className="heading-section text-2xl md:text-3xl text-slate-900"
                            >
                                Not chatbots. Not voicebots. A system that makes your organization measurably
                                better at the decisions it makes every day.
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <div className="mt-16 flex flex-wrap gap-3">
                            <button
                                onClick={openDemo}
                                className="pill-dark"
                                data-testid="manifesto-book-demo"
                            >
                                See augmentation in your operations{" "}
                                <i className="fa-solid fa-arrow-right text-xs" />
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
